import { PageHero, Reveal, Eyebrow, ArrowButton } from "@/components/bits";
import { PARTNER_TYPES, COLLABORATORS, PROCESS_STEPS } from "@/data/content";
import { COLLABORATOR_LOGOS } from "@/data/logos";

export default function Partnerships() {
  return (
    <main data-testid="partnerships-page">
      <PageHero
        testid="partnerships-hero"
        eyebrow="Partnerships"
        title={
          <>
            We build <span className="text-tnn-purple">with</span> people, not around them.
          </>
        }
        lead="Some of our strongest work has come from partnerships. TNN Africa works alongside founders, healthcare organisations, technology companies, clinicians, food businesses and institutions to bring nutrition expertise into products and services."
      />

      <section className="bg-white py-16 lg:py-24" data-testid="who-we-work-with">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Who We Work With</Eyebrow>
          </Reveal>
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-5">
            {PARTNER_TYPES.map((p, i) => (
              <Reveal key={p.title} delay={i * 0.06}>
                <div
                  data-testid={`partner-type-${i}`}
                  className="group bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-8 h-full transition-colors duration-300 hover:bg-tnn-purple"
                >
                  <span className="w-8 h-8 rounded-full bg-white group-hover:bg-white/15 text-tnn-purple group-hover:text-white text-xs font-bold flex items-center justify-center transition-colors duration-300">
                    {i + 1}
                  </span>
                  <h3 className="mt-5 font-display text-xl font-semibold text-tnn-ink group-hover:text-white transition-colors duration-300">
                    {p.title}
                  </h3>
                  <p className="mt-2 text-sm leading-relaxed text-tnn-muted group-hover:text-white/75 transition-colors duration-300">
                    {p.desc}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-tnn-warm py-16 lg:py-24" data-testid="collaborations">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>Selected Collaborations</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink">
              Built with an ecosystem of partners
            </h2>
            <p className="mt-3 text-sm text-tnn-muted">Selected organisations and ventures we've built with</p>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-5">
            {COLLABORATORS.map((name, i) => (
              <Reveal key={name} delay={i * 0.05}>
                <div
                  data-testid={`collaborator-${i}`}
                  className="h-28 lg:h-32 bg-white border border-tnn-ink/6 rounded-3xl flex items-center justify-center px-6 transition-colors duration-300 hover:border-tnn-purple/30"
                  style={COLLABORATOR_LOGOS[name]?.bg ? { background: COLLABORATOR_LOGOS[name].bg } : undefined}
                >
                  {COLLABORATOR_LOGOS[name] ? (
                    <img
                      src={COLLABORATOR_LOGOS[name].src}
                      alt={`${name} logo`}
                      loading="lazy"
                      className={`${COLLABORATOR_LOGOS[name].cls || "max-h-12"} max-w-[75%] object-contain`}
                    />
                  ) : (
                    <span className="font-display font-semibold text-lg text-tnn-ink/60 text-center">{name}</span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-white py-16 lg:py-24" data-testid="partnership-process">
        <div className="max-w-6xl mx-auto px-6">
          <Reveal>
            <Eyebrow>How We Work</Eyebrow>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl font-bold tracking-tight text-tnn-ink max-w-xl leading-[1.15]">
              A partnership process built for real-world delivery.
            </h2>
          </Reveal>
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-5 gap-4">
            {PROCESS_STEPS.map((s, i) => (
              <Reveal key={s.num} delay={i * 0.06}>
                <div
                  data-testid={`process-step-${i}`}
                  className="bg-tnn-warm border border-tnn-ink/6 rounded-3xl p-6 h-full transition-colors duration-300 hover:border-tnn-purple/30"
                >
                  <span className="text-xs font-bold text-tnn-purple/50">{s.num}</span>
                  <h3 className="mt-3 font-display text-lg font-semibold text-tnn-ink">{s.title}</h3>
                  <p className="mt-1.5 text-sm text-tnn-muted leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.25} className="mt-12">
            <ArrowButton to="/contact" testid="process-cta">Start a Partnership Conversation</ArrowButton>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
